import { formatDate, formatDateTime } from '../../../utils/dateFormat';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { serviceAgentService, DelegationDto, AuditLogDto, PageResponse } from '../../../services/serviceAgent.service';
import { delegationService } from '../../../services/delegation.service';
import { DelegationScope } from '../../../models/DelegationDto';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { PersonSpFilterBanner, guidEq } from '../../../components/PersonSpFilterBanner/PersonSpFilterBanner';
import { investorService, PersonAccountGroup } from '../../../services/investor.service';
import { profileService, ServiceProviderDto } from '../../../services/profile.service';
import { SCOPE_LABELS, permissionsForScope } from '../../../utils/delegationScope';
import './DelegationManagement.scss';

/**
 * Scope options with helper text — mirrors the Investor_COnsent_SA.html design. Each scope
 * includes the one before it; the scope alone decides the permission ticks (2026-10-05).
 */
const SCOPE_OPTIONS: { value: DelegationScope; label: string; description: string }[] = [
  { value: 'VIEW_ONLY', label: SCOPE_LABELS.VIEW_ONLY, description: "Can see your information. Can't change anything." },
  { value: 'CKYC_ONLY', label: SCOPE_LABELS.CKYC_ONLY, description: 'Personal, contact, passport, residential and tax information.' },
  { value: 'ONBOARDING_ONLY', label: SCOPE_LABELS.ONBOARDING_ONLY, description: 'Everything from Information through the Onboarding forms, except Final Submit.' },
  { value: 'FULL_ONBOARDING', label: SCOPE_LABELS.FULL_ONBOARDING, description: 'Complete access, including Final Submit, verification and physical submission.' },
];

const STATUS_BADGE: Record<string, string> = {
  PENDING: 'bg-warning text-dark',
  ACTIVE: 'bg-success',
  REVOKED: 'bg-secondary',
  REJECTED: 'bg-danger',
  EXPIRED: 'bg-secondary',
};

/** Closed consents: history rows the investor may re-authorise (2026-11-04). */
const isClosed = (status?: string) => status === 'REVOKED' || status === 'REJECTED' || status === 'EXPIRED';

/** The permission ticks are derived from the scope (permissionsForScope), never stored here. */
interface ConsentForm {
  scope: DelegationScope;
  consentGiven: boolean;
}

export const DelegationManagement: React.FC = () => {
  const [delegations, setDelegations] = useState<DelegationDto[]>([]);
  // PM2: Accounts (person) × Service Provider filters — same banner as My Documents.
  const [personGroups, setPersonGroups] = useState<PersonAccountGroup[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProviderDto[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | 'ALL'>('ALL');
  const [selectedSpRef, setSelectedSpRef] = useState<string>('ALL');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<ConsentForm | null>(null);
  // ACTIVE consent: view mode (Edit Access / Revoke Consent) until Edit Access is clicked,
  // then edit mode (Save Changes / Cancel). Any card switch or reload returns to view mode.
  const [editing, setEditing] = useState(false);
  // Closed consent (revoked / rejected / expired): "Re-authorise" switches the panel into consent
  // mode (scope + fresh tick); confirming creates a NEW active row, the closed row stays as history.
  const [reauthorising, setReauthorising] = useState(false);

  // ── Activity (transparency log: what the agent did on my account) ─────────
  const [activeTab, setActiveTab] = useState<'consents' | 'activity'>('consents');
  const [activity, setActivity] = useState<PageResponse<AuditLogDto> | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(0);

  useEffect(() => {
    fetchDelegations();
    // PM2 filter data — best-effort; failures just leave the dropdowns at All.
    investorService.getPersonAccounts()
      .then((g) => setPersonGroups(g || []))
      .catch(() => setPersonGroups([]));
    profileService.getServiceProviders()
      .then((sp) => setServiceProviders(sp || []))
      .catch(() => setServiceProviders([]));
  }, []);

  useEffect(() => {
    if (activeTab === 'activity') fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activityPage]);

  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      setActivity(await serviceAgentService.getMyAgentActivity(activityPage, 20));
    } catch {
      toast.error('Failed to load agent activity');
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchDelegations = async (keepSelection = false, selectId: number | null = null) => {
    try {
      const data = await serviceAgentService.getMyDelegations();
      setDelegations(data);
      // selectId: the row a just-finished action created (re-authorise) — select it, not the old card.
      const wanted = selectId != null ? data.find(d => d.id === selectId) : undefined;
      if (wanted) {
        selectDelegation(wanted);
      } else if (!keepSelection && data.length > 0) {
        selectDelegation(data[0]);
      } else if (keepSelection && selectedId != null) {
        const refreshed = data.find(d => d.id === selectedId);
        if (refreshed) selectDelegation(refreshed);
      }
    } catch {
      toast.error('Failed to load delegations');
    } finally {
      setLoading(false);
    }
  };

  const selectDelegation = (d: DelegationDto) => {
    setSelectedId(d.id);
    setForm({
      scope: d.scope as DelegationScope,
      // Active delegations were already consented; pending requires a fresh tick.
      consentGiven: d.status === 'ACTIVE',
    });
    setEditing(false);
    setReauthorising(false);
  };

  const selected = delegations.find(d => d.id === selectedId) || null;
  const formPerms = permissionsForScope(form?.scope);
  // The scope can be chosen before consenting, and changed on an ACTIVE consent only after Edit Access.
  const scopeEditable = selected?.status === 'PENDING'
    || (selected?.status === 'ACTIVE' && editing)
    || (isClosed(selected?.status) && reauthorising);

  const setField = (patch: Partial<ConsentForm>) => setForm(f => (f ? { ...f, ...patch } : f));

  /** Edit mode's Cancel: put the saved scope back and return to view mode. */
  const handleCancelEdit = () => {
    if (selected) setField({ scope: selected.scope as DelegationScope });
    setEditing(false);
  };

  /** Closed consent → consent mode: scope pre-filled from the closed row, a fresh tick is required. */
  const handleStartReauthorise = () => {
    if (!selected) return;
    setField({ scope: selected.scope as DelegationScope, consentGiven: false });
    setEditing(false);
    setReauthorising(true);
  };

  const handleCancelReauthorise = () => {
    if (selected) setField({ scope: selected.scope as DelegationScope, consentGiven: false });
    setReauthorising(false);
  };

  /** Re-authorise = a NEW consent row for the same agent; the closed row stays as history (2026-11-04). */
  const handleReauthorise = async () => {
    if (!selected || !form) return;
    if (!form.consentGiven) {
      toast.warn('Please tick the consent box to re-authorise this Service Agent.');
      return;
    }
    const customizations = { scope: form.scope, ...permissionsForScope(form.scope) };
    setProcessing(selected.id);
    try {
      const created = await delegationService.reauthoriseDelegation(selected.id, customizations);
      // The server is idempotent: an open consent for the same agent is returned instead of a new row.
      if (delegations.some(d => d.id === created.id)) {
        toast.info('An open consent for this Service Agent already exists — showing it.');
      } else {
        toast.success('Consent re-authorised — the Service Agent can act on your behalf again.');
      }
      setReauthorising(false);
      await fetchDelegations(true, created.id);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to re-authorise');
    } finally {
      setProcessing(null);
    }
  };

  const handleSave = async () => {
    if (!selected || !form) return;
    if (selected.status === 'PENDING' && !form.consentGiven) {
      toast.warn('Please tick the consent box to authorise this Service Agent.');
      return;
    }
    // Save Changes with the scope unchanged: nothing to send, just leave edit mode.
    if (selected.status === 'ACTIVE' && form.scope === selected.scope) {
      setEditing(false);
      return;
    }
    // The backend derives the same flags from the scope; sending them keeps the request explicit.
    const customizations = { scope: form.scope, ...permissionsForScope(form.scope) };
    setProcessing(selected.id);
    try {
      if (selected.status === 'PENDING') {
        await delegationService.acceptDelegation(selected.id, customizations);
        toast.success('Consent saved — the Service Agent can now act on your behalf.');
      } else {
        await delegationService.updateDelegation(selected.id, customizations);
        toast.success('Permissions updated.');
      }
      setEditing(false);
      await fetchDelegations(true);
    } catch (error: any) {
      const msg: string = error?.response?.data?.message || '';
      toast.error(msg || 'Failed to save consent');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!globalThis.confirm(`Reject the assignment from ${selected.serviceAgentName}?`)) return;
    setProcessing(selected.id);
    try {
      await delegationService.rejectDelegation(selected.id, 'Declined by investor');
      toast.info(`Assignment rejected: ${selected.serviceAgentName}`);
      await fetchDelegations();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const handleRevoke = async () => {
    if (!selected) return;
    if (!globalThis.confirm(`Revoke access for ${selected.serviceAgentName}? They will immediately lose access to your account.`)) return;
    setProcessing(selected.id);
    try {
      await serviceAgentService.revokeDelegation(selected.id);
      toast.success('Consent revoked.');
      await fetchDelegations();
    } catch {
      toast.error('Failed to revoke');
    } finally {
      setProcessing(null);
    }
  };

  const fmtDate = (d?: string) => (d ? formatDate(d) : null);
  const fmtDateTime = (d?: any) => {
    if (!d) return '—';
    // Backend may serialize LocalDateTime as an ISO string OR a [y,mo,day,h,mi,s] array.
    if (Array.isArray(d)) {
      const [y, mo = 1, day = 1, h = 0, mi = 0, s = 0] = d;
      return formatDateTime(new Date(y, mo - 1, day, h, mi, s));
    }
    return formatDateTime(d);
  };

  // PM2: fail-open AND filter — a delegation is excluded only when POSITIVELY attributed
  // to a different person/SP (identity-wide rows, null spRef, pre-person rows never hide).
  const selfPersonId = personGroups[0]?.personId ?? null;
  const filteredDelegations = delegations.filter((d) => {
    if (selectedPersonId !== 'ALL') {
      const pid = d.personId ?? selfPersonId;
      if (pid !== null && pid !== selectedPersonId) return false;
    }
    if (selectedSpRef !== 'ALL' && d.spRef && !guidEq(d.spRef, selectedSpRef)) return false;
    return true;
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="layout-wrapper">
      <main className="container-fluid dashboard-container-main">
        <div className="delegation-management">

          {/* PM2: dashboard-style banner with the Accounts × SP filters */}
          <div className="mb-3">
            <PersonSpFilterBanner
              title="My Permissions"
              personGroups={personGroups}
              serviceProviders={serviceProviders}
              selectedPersonId={selectedPersonId}
              selectedSpRef={selectedSpRef}
              onPersonChange={setSelectedPersonId}
              onSpChange={setSelectedSpRef}
            />
          </div>

          {/* Tabs: manage consents  |  see what the agent did on my account */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'consents' ? 'active' : ''}`}
                onClick={() => setActiveTab('consents')}
              >
                <i className="bi bi-shield-check me-1"></i> Consents
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                <i className="bi bi-clock-history me-1"></i> Activity
              </button>
            </li>
          </ul>

          {activeTab === 'consents' ? (
          <div className="row g-3">
            {/* ── Left: Existing Consents ─────────────────────────────── */}
            <div className="col-lg-5">
              <div className="card h-100">
                <div className="card-header bg-white fw-bold">Assigned Service Agent</div>
                <div className="card-body p-2">
                  {filteredDelegations.length === 0 && (
                    <div className="text-center text-muted small py-4">
                      {delegations.length > 0
                        ? 'No Service Agent matches the selected filters.'
                        : (<>No Service Agent assigned yet.<br />
                          Your Service Provider will assign a Service Agent to assist you.</>)}
                    </div>
                  )}
                  {filteredDelegations.map(d => {
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => selectDelegation(d)}
                        className={`w-100 text-start border rounded p-2 mb-2 d-block ${selectedId === d.id ? 'border-primary' : 'border-light-subtle'}`}
                        style={{ background: selectedId === d.id ? '#f1f5f9' : '#fff', cursor: 'pointer' }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <span className="fw-bold text-dark">{d.serviceAgentName || 'Service Agent'}</span>
                          <span className={`badge ${STATUS_BADGE[d.status || ''] || 'bg-secondary'}`}>
                            {d.status || (d.isActive ? 'ACTIVE' : '—')}
                          </span>
                        </div>
                        {d.serviceAgentEmail && <div className="small text-muted">{d.serviceAgentEmail}</div>}
                        {d.serviceAgentMobile && <div className="small text-muted">📱 {d.serviceAgentMobile}</div>}
                        {d.serviceAgentCode && <div className="small text-muted">Code: {d.serviceAgentCode}</div>}
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {/* Account chip: whose journey this delegation covers (person layer) */}
                          {(d.personName || personGroups[0]?.displayName) && (
                            <span className="badge border" style={{ background: '#fdf6e2', color: '#b0842b', borderColor: '#b0842b40' }}>
                              <i className="bi bi-person me-1"></i>
                              {d.personName || personGroups[0]?.displayName}
                            </span>
                          )}
                          {/* Access level and permissions are not repeated here: the panel on the
                              right shows both for the selected card (2026-10-05). */}
                          {(d.productName || d.relatedRef) ? (
                            <span
                              className="badge border"
                              style={{ background: '#eef6ff', color: '#1e40af', borderColor: '#bfdbfe' }}
                              title={d.serviceProviderName ? `Service Provider: ${d.serviceProviderName}` : undefined}
                            >
                              🔒 {d.productName || 'Primary account'}
                            </span>
                          ) : (
                            <span className="badge bg-light text-dark border" title="Applies to all of your products/accounts">
                              All accounts
                            </span>
                          )}
                        </div>
                        <div className="small text-muted mt-1">
                          Valid {fmtDate(d.validFrom) || 'immediately'} → {fmtDate(d.validTo) || 'no expiry'}
                        </div>
                        {isClosed(d.status) && d.revokedAt && (
                          <div className="small text-muted mt-1">
                            {d.status === 'EXPIRED' ? 'Expired' : d.status === 'REJECTED' ? 'Rejected' : 'Revoked'}{' '}
                            {fmtDateTime(d.revokedAt)}
                          </div>
                        )}
                        {d.notes && <div className="small text-muted mt-1"><em>{d.notes}</em></div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Right: Grant or Update Consent ──────────────────────── */}
            <div className="col-lg-7">
              <div className="card h-100">
                <div className="card-header bg-white fw-bold">Grant or Update Consent</div>
                <div className="card-body">
                  {!selected || !form ? (
                    <div className="text-center text-muted small py-5">
                      Select a Service Agent on the left to view or change their permissions.
                    </div>
                  ) : (
                    <>
                      {/* Agent details */}
                      <div className="mb-3">
                        <div className="fw-bold">{selected.serviceAgentName}</div>
                        <div className="small text-muted">{selected.serviceAgentEmail}</div>
                        {selected.serviceAgentMobile && <div className="small text-muted">📱 {selected.serviceAgentMobile}</div>}
                        <div className="small text-muted">Code: {selected.serviceAgentCode}</div>
                        <div className="small mt-1">
                          <span className="text-muted">Authorised for: </span>
                          <strong>
                            {selected.productName
                              || (selected.relatedRef ? 'Primary account' : 'All accounts / all products')}
                          </strong>
                          {selected.serviceProviderName && (
                            <span className="text-muted"> · {selected.serviceProviderName}</span>
                          )}
                        </div>
                      </div>

                      {/* Scope + helper text */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold mb-1">Access scope</label>
                        <select
                          className="form-select"
                          value={form.scope}
                          disabled={!scopeEditable}
                          onChange={(e) => setField({ scope: e.target.value as DelegationScope })}
                        >
                          {SCOPE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <div className="form-text">
                          {SCOPE_OPTIONS.find(o => o.value === form.scope)?.description}
                        </div>
                      </div>

                      {/* Permissions — display only: the scope above decides every tick. */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold mb-1">Permissions</label>
                        {([
                          { key: 'canViewProfile', label: 'Can view profile & documents' },
                          { key: 'canEditKyc', label: 'Can edit KYC / personal information' },
                          { key: 'canUploadDocuments', label: 'Can upload documents on my behalf' },
                          { key: 'canSubmitForms', label: 'Can submit forms on my behalf' },
                        ] as const).map(p => (
                          <div className="form-check" key={p.key}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={p.key}
                              checked={formPerms[p.key]}
                              readOnly
                              disabled
                            />
                            <label className="form-check-label small" htmlFor={p.key}>{p.label}</label>
                          </div>
                        ))}
                        <div className="form-text">
                          Set by the access scope. Change the scope to change these permissions.
                        </div>
                      </div>

                      {/* Validity + reference (read-only) */}
                      <div className="row mb-3">
                        <div className="col-6">
                          <div className="small text-muted">Valid from</div>
                          <div className="small">{fmtDate(selected.validFrom) || 'Immediately'}</div>
                        </div>
                        <div className="col-6">
                          <div className="small text-muted">Valid until</div>
                          <div className="small">{fmtDate(selected.validTo) || 'No expiry'}</div>
                        </div>
                      </div>
                      {selected.notes && (
                        <div className="alert alert-secondary small py-2">
                          <strong>Reference / Notes:</strong> {selected.notes}
                        </div>
                      )}

                      {/* Consent statement: a pending consent and a re-authorisation both need a fresh tick */}
                      {(selected.status === 'PENDING' || reauthorising) && (
                        <div className="form-check mb-3 p-2 border rounded bg-light">
                          <input
                            className="form-check-input ms-0 me-2"
                            type="checkbox"
                            id="consentGiven"
                            checked={form.consentGiven}
                            onChange={(e) => setField({ consentGiven: e.target.checked })}
                          />
                          <label className="form-check-label small" htmlFor="consentGiven">
                            I authorise <strong>{selected.serviceAgentName}</strong> to access my data and act on my
                            behalf within the scope and permissions selected above. All their actions are logged, and
                            I can revoke this at any time.
                          </label>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="d-flex gap-2">
                        {selected.status === 'PENDING' && (
                          <>
                            <button className="btn btn-success flex-fill" onClick={handleSave} disabled={processing === selected.id}>
                              {processing === selected.id ? 'Saving…' : 'Confirm & Save Consent'}
                            </button>
                            <button className="btn btn-outline-danger" onClick={handleReject} disabled={processing === selected.id}>
                              Reject
                            </button>
                          </>
                        )}
                        {/* ACTIVE, view mode: the scope is locked until Edit Access. */}
                        {selected.status === 'ACTIVE' && !editing && (
                          <>
                            <button className="btn btn-primary flex-fill" onClick={() => setEditing(true)} disabled={processing === selected.id}>
                              Edit Access
                            </button>
                            <button className="btn btn-outline-danger" onClick={handleRevoke} disabled={processing === selected.id}>
                              Revoke Consent
                            </button>
                          </>
                        )}
                        {/* ACTIVE, edit mode: Revoke is hidden so it can't be hit mid-edit. */}
                        {selected.status === 'ACTIVE' && editing && (
                          <>
                            <button className="btn btn-primary flex-fill" onClick={handleSave} disabled={processing === selected.id}>
                              {processing === selected.id ? 'Saving…' : 'Save Changes'}
                            </button>
                            <button className="btn btn-outline-secondary" onClick={handleCancelEdit} disabled={processing === selected.id}>
                              Cancel
                            </button>
                          </>
                        )}
                        {/* Closed consent, view mode: history line + Re-authorise (hidden when the agent is inactive). */}
                        {isClosed(selected.status) && !reauthorising && (
                          <>
                            <div className="text-muted small flex-fill align-self-center">
                              This consent was {selected.status?.toLowerCase()}
                              {selected.revokedAt ? ` on ${fmtDateTime(selected.revokedAt)}` : ''}.
                              {selected.serviceAgentActive === false && ' This Service Agent is no longer active.'}
                            </div>
                            {selected.reauthorisable && (
                              <button className="btn btn-primary" onClick={handleStartReauthorise} disabled={processing === selected.id}>
                                Re-authorise
                              </button>
                            )}
                          </>
                        )}
                        {/* Closed consent, consent mode: pick the scope, tick, confirm → a new ACTIVE consent. */}
                        {isClosed(selected.status) && reauthorising && (
                          <>
                            <button className="btn btn-success flex-fill" onClick={handleReauthorise} disabled={processing === selected.id}>
                              {processing === selected.id ? 'Saving…' : 'Confirm & Re-authorise'}
                            </button>
                            <button className="btn btn-outline-secondary" onClick={handleCancelReauthorise} disabled={processing === selected.id}>
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          ) : (
            /* ── Activity: what the Service Agent did on my account ───── */
            <div className="card">
              <div className="card-header bg-white fw-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-clock-history me-2"></i>Service Agent Activity on Your Account</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={fetchActivity} disabled={activityLoading} title="Refresh">
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
              <div className="card-body p-0">
                {activityLoading ? (
                  <div className="py-5"><LoadingSpinner /></div>
                ) : !activity || activity.content.length === 0 ? (
                  <div className="text-center text-muted small py-5">
                    No agent activity yet.<br />
                    When a Service Agent views or updates your account, every action shows up here.
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0 small">
                        <thead className="table-light">
                          <tr>
                            <th>Date / Time</th>
                            <th>Service Agent</th>
                            <th>Action</th>
                            <th>Category</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activity.content.map(log => (
                            <tr key={log.id}>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(log.createdAt)}</td>
                              <td>{log.serviceAgentName || '—'}</td>
                              <td>{(log.actionType || '').replace(/_/g, ' ')}</td>
                              <td><span className="badge bg-light text-dark border">{log.actionCategory}</span></td>
                              <td>
                                {log.success
                                  ? <span className="badge bg-success">Success</span>
                                  : <span className="badge bg-danger" title={log.errorMessage}>Failed</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {activity.totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center p-2 border-top">
                        <button className="btn btn-sm btn-outline-secondary" disabled={activityPage === 0} onClick={() => setActivityPage(p => p - 1)}>← Prev</button>
                        <span className="small text-muted">Page {activityPage + 1} of {activity.totalPages}</span>
                        <button className="btn btn-sm btn-outline-secondary" disabled={activityPage >= activity.totalPages - 1} onClick={() => setActivityPage(p => p + 1)}>Next →</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DelegationManagement;
